import React, { useState } from 'react';
import { getIntentDistribution, getIntentStrategy, getIntentIcon, SearchIntent } from '../services/searchIntent';
import { EnhancedSearchConsoleData } from '../types';

// Gradient SVG Icons Component
const GradientIcon: React.FC<{ type: string; size?: string }> = ({ type, size = "w-5 h-5" }) => {
  const iconPaths: { [key: string]: string } = {
    chart: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z",
    target: "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    refresh: "M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
  };

  return (
    <svg className={size} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id={`gradient-${type}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ec4899" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <path
        d={iconPaths[type] || iconPaths.target}
        fill={`url(#gradient-${type})`}
        fillRule="evenodd"
        clipRule="evenodd"
      />
    </svg>
  );
};

interface SearchIntentPanelProps {
  data: EnhancedSearchConsoleData[];
  onFilterByIntent?: (intent: SearchIntent | null) => void;
}

const SearchIntentPanel: React.FC<SearchIntentPanelProps> = ({ data, onFilterByIntent }) => {
  const [selectedIntent, setSelectedIntent] = useState<SearchIntent | null>(null);
  const [showStrategies, setShowStrategies] = useState(false);

  if (data.length === 0) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-gray-200 p-6 mb-6">
        <div className="text-center">
          <div className="mb-2">
            <GradientIcon type="chart" size="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mt-2">Search Intent Analysis</h2>
          <p className="text-gray-600 mt-1">Load your SEO data to analyze search intent patterns and get strategy recommendations.</p>
        </div>
      </div>
    );
  }

  const distribution = getIntentDistribution(data);
  const totalKeywords = distribution.total;

  // Calculate percentages
  const intentPercentages = {
    informational: Math.round((distribution.informational / totalKeywords) * 100),
    commercial: Math.round((distribution.commercial / totalKeywords) * 100),
    transactional: Math.round((distribution.transactional / totalKeywords) * 100),
    navigational: Math.round((distribution.navigational / totalKeywords) * 100)
  };

  const handleIntentFilter = (intent: SearchIntent) => {
    const newIntent = selectedIntent === intent ? null : intent;
    setSelectedIntent(newIntent);
    if (onFilterByIntent) {
      onFilterByIntent(newIntent);
    }
  };

  const intentCards = [
    {
      intent: 'informational' as SearchIntent,
      title: 'Informational',
      description: 'Questions, guides, tutorials',
      bgColor: 'bg-blue-50',
      hoverColor: 'hover:bg-blue-100',
      textColor: 'text-blue-900',
      borderColor: selectedIntent === 'informational' ? 'border-blue-500' : 'border-blue-200',
      progressColor: 'bg-blue-500'
    },
    {
      intent: 'commercial' as SearchIntent,
      title: 'Commercial',
      description: 'Research, comparisons, reviews',
      bgColor: 'bg-amber-50',
      hoverColor: 'hover:bg-amber-100',
      textColor: 'text-amber-900',
      borderColor: selectedIntent === 'commercial' ? 'border-amber-500' : 'border-amber-200',
      progressColor: 'bg-amber-500'
    },
    {
      intent: 'transactional' as SearchIntent,
      title: 'Transactional',
      description: 'Purchase, buy, local services',
      bgColor: 'bg-green-50',
      hoverColor: 'hover:bg-green-100',
      textColor: 'text-green-900',
      borderColor: selectedIntent === 'transactional' ? 'border-green-500' : 'border-green-200',
      progressColor: 'bg-green-500'
    },
    {
      intent: 'navigational' as SearchIntent,
      title: 'Navigational',
      description: 'Brand, website, login pages',
      bgColor: 'bg-purple-50',
      hoverColor: 'hover:bg-purple-100',
      textColor: 'text-purple-900',
      borderColor: selectedIntent === 'navigational' ? 'border-purple-500' : 'border-purple-200',
      progressColor: 'bg-purple-500'
    }
  ];

  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <GradientIcon type="chart" size="w-8 h-8" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Search Intent Analysis</h2>
            <p className="text-sm text-gray-600">Understand the intent behind your keywords</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowStrategies(!showStrategies)}
            className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors text-sm font-medium"
          >
            {showStrategies ? 'Hide' : 'Show'} Strategies
          </button>
          {selectedIntent && (
            <button
              onClick={() => handleIntentFilter(selectedIntent)}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm"
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {/* Intent Distribution Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {intentCards.map((card) => {
          const count = distribution[card.intent];
          const percentage = intentPercentages[card.intent];
          const isSelected = selectedIntent === card.intent;
          
          return (
            <div
              key={card.intent}
              onClick={() => handleIntentFilter(card.intent)}
              className={`${card.bgColor} ${card.hoverColor} border-2 ${card.borderColor} rounded-lg p-4 cursor-pointer transition-all transform ${isSelected ? 'scale-105 shadow-lg' : 'hover:scale-102'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getIntentIcon(card.intent)}</span>
                  <h3 className={`font-semibold ${card.textColor}`}>{card.title}</h3>
                </div>
                {isSelected && (
                  <span className="text-xs bg-white px-2 py-1 rounded-full font-medium text-gray-600">
                    Filtered
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-3">{card.description}</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-2xl font-bold ${card.textColor}`}>{count.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">keywords</p>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-semibold ${card.textColor}`}>{percentage}%</p>
                  <p className="text-xs text-gray-500">of total</p>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`${card.progressColor} h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Strategy Recommendations */}
      {showStrategies && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>💡</span> Strategy Recommendations
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(['informational', 'commercial', 'transactional', 'navigational'] as SearchIntent[]).map((intent) => {
              const strategy = getIntentStrategy(intent);
              const intentData = intentCards.find(c => c.intent === intent)!;
              const count = distribution[intent];
              
              if (count === 0) return null;
              
              return (
                <div key={intent} className={`${intentData.bgColor} rounded-lg p-4 border border-gray-200`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">{getIntentIcon(intent)}</span>
                    <h4 className={`font-semibold ${intentData.textColor}`}>{strategy.title}</h4>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{strategy.description}</p>
                  
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">Action Items:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {strategy.actions.map((action, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-green-500 mt-0.5 flex-shrink-0">•</span>
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedIntent && (
        <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Filtered:</span> Showing only {selectedIntent} intent keywords. 
            <button 
              onClick={() => handleIntentFilter(selectedIntent)}
              className="text-blue-600 hover:text-blue-800 ml-1"
            >
              Clear filter
            </button> to see all results.
          </p>
        </div>
      )}
    </div>
  );
};

export default SearchIntentPanel;